import { RawNodeDatum } from 'react-d3-tree'
import { VerdictColorVariant, SignalVerdict } from '../../types'
import { PredictiveAIVerdict } from '../osint/opswat-reputation/helpers'

export type ReportOverviewFilterStat = {
  types: {
    verdict?: {
      [key in SignalVerdict]?: number
    }
    origin?: { [key: string]: number }
    mitre?: { [key: string]: number }
    mbc?: { [key: string]: number }
    tags?: { [key: string]: number }
  }
  total?: number
}

export enum ScanStageType {
  Reputation_service = 'reputation_service',
  Deep_static_analysis = 'deep_static_analysis',
  Dynamic_fast_pass = 'dynamic_fast_pass',
}

export type StructuredContentItem = {
  type: string
  content: string[]
  isNested?: boolean
}

export type TotalAndMaliciousCount = {
  total: number
  malicious: number
}

export type TotalAndRiskyCount = {
  total: number
  risky: number
}

export type ReputationStageInfo = {
  isEnabled?: boolean
  duration?: number
  checksCount?: number
  iocsCount?: TotalAndMaliciousCount
  threatIndicatorsCount?: TotalAndMaliciousCount
}

export type StaticStageInfo = {
  duration?: number
  obfuscationTechniquesCount?: number
  iocsCount?: TotalAndMaliciousCount
  threatIndicatorsCount?: TotalAndMaliciousCount
}

export type DynamicStageInfo = {
  duration?: number
  actionsCount?: number
  iocsCount?: TotalAndMaliciousCount
  threatIndicatorsCount?: TotalAndMaliciousCount
}

export type ScanStagesInfo = {
  [ScanStageType.Reputation_service]: ReputationStageInfo
  [ScanStageType.Deep_static_analysis]: StaticStageInfo
  [ScanStageType.Dynamic_fast_pass]: DynamicStageInfo
}

export type TopCategory = {
  variant: VerdictColorVariant
  verdicts: SignalVerdict[]
  count: number
  text?: string
}

export type EmulationCounts = {
  process: number
  network: number
  other: number
}

export const threatIndicatorsIcons = {
  [ScanStageType.Reputation_service]: 'threat-intel',
  [ScanStageType.Deep_static_analysis]: 'analysis',
  [ScanStageType.Dynamic_fast_pass]: 'override',
}

export type FlattenTree = {
  tree: RawNodeDatum
  title: string
  id: string
  type: string
}

export type EmulationDataToFlatten = {
  extracted?: { [key: string]: any }
  downloaded?: { [key: string]: any }
  input?: { [key: string]: any }
}

export enum AetherPipelineStep {
  ThreatReputation = 'threat_reputation',
  StaticAnalysis = 'static_analysis',
  DynamicAnalysis = 'dynamic_analysis',
  ThreatScoring = 'threat_scoring',
  ThreatHunting = 'threat_hunting',
}

export const AETHER_LAYER_LABELS: Record<AetherPipelineStep, string> = {
  [AetherPipelineStep.ThreatReputation]: 'Threat Reputation',
  [AetherPipelineStep.StaticAnalysis]: 'Static Analysis',
  [AetherPipelineStep.DynamicAnalysis]: 'Dynamic Analysis',
  [AetherPipelineStep.ThreatScoring]: 'Threat Scoring',
  [AetherPipelineStep.ThreatHunting]: 'Threat Hunting',
}

export type VerdictCount = {
  [key in SignalVerdict]?: number
}

export type ThreatReputationStepInfo = {
  verdictIocsCount: number
  totalIocsCount: number
  verdict: SignalVerdict
}

export type StaticAnalysisStepInfo = {
  predictiveAiVerdict?: PredictiveAIVerdict
  totalAvs: number
  detectedAvs: number
  avVerdict: SignalVerdict
}

export type DynamicAnalysisStepInfo = {
  verdictThreatIndicatorsCount: number
  totalThreatIndicatorsCount: number
  threatIndicatorsVerdict: SignalVerdict
  verdictExtractedFilesCount: number
  totalExtractedFilesCount: number
  extractedFilesVerdict: SignalVerdict
}

export type ThreatScoringStepInfo = {
  verdict: SignalVerdict | null
}

export type ThreatHuntingStepInfo = {
  similarReportsCount: number
  verdict: SignalVerdict
}

export type AetherPipelineInfo = {
  [AetherPipelineStep.ThreatReputation]: ThreatReputationStepInfo
  [AetherPipelineStep.StaticAnalysis]: StaticAnalysisStepInfo
  [AetherPipelineStep.DynamicAnalysis]: DynamicAnalysisStepInfo
  [AetherPipelineStep.ThreatScoring]: ThreatScoringStepInfo
  [AetherPipelineStep.ThreatHunting]: ThreatHuntingStepInfo
}
